(() => {
  const STYLE_CLASS = 'buga-smart-sticky-atc';
  const CONTROLS_CLASS = 'buga-sticky-controls';

  const cssEscape = (value) => {
    if (window.CSS?.escape) return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const getVariantPicker = (sticky) => {
    const productId = sticky.dataset.productId;
    if (!productId) return null;
    return document.querySelector(`variant-picker[data-product-id="${cssEscape(productId)}"]`);
  };

  const getProductForm = (sticky) => {
    const productId = sticky.dataset.productId;
    if (!productId) return null;
    const section = sticky.closest('.shopify-section');
    return section?.querySelector(`product-form-component[data-product-id="${cssEscape(productId)}"]`) ?? null;
  };

  const selectNativeOption = (sticky, optionName, value) => {
    const picker = getVariantPicker(sticky);
    if (!picker) return;

    const radios = Array.from(picker.querySelectorAll('input[data-option-name]'));
    const radio = radios.find((input) =>
      input instanceof HTMLInputElement &&
      input.dataset.optionName === optionName &&
      input.value === value
    );

    if (radio instanceof HTMLInputElement) {
      if (!radio.checked) radio.click();
      return;
    }

    const selects = Array.from(picker.querySelectorAll('select'));
    const nativeSelect = selects.find((select) =>
      select instanceof HTMLSelectElement &&
      Array.from(select.options).some((option) => option.dataset.optionName === optionName)
    );

    if (nativeSelect instanceof HTMLSelectElement) {
      nativeSelect.value = value;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const createSelectFromRadios = (sticky, radioInputs) => {
    const first = radioInputs.find((input) => input instanceof HTMLInputElement);
    const optionName = first?.dataset.optionName;
    if (!optionName || radioInputs.length < 2) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'buga-sticky-control';

    const select = document.createElement('select');
    select.className = 'buga-sticky-select';
    select.dataset.optionName = optionName;
    select.setAttribute('aria-label', optionName);

    for (const input of radioInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const option = document.createElement('option');
      option.value = input.value;
      option.textContent = input.value;
      option.selected = input.checked;
      option.disabled = input.dataset.optionAvailable === 'false';
      select.appendChild(option);
    }

    select.addEventListener('change', () => selectNativeOption(sticky, optionName, select.value));
    wrapper.appendChild(select);
    return wrapper;
  };

  const createSelectFromNativeSelect = (nativeSelect) => {
    if (!(nativeSelect instanceof HTMLSelectElement) || nativeSelect.options.length < 2) return null;

    const selected = nativeSelect.selectedOptions[0];
    const optionName = selected?.dataset.optionName || nativeSelect.name || 'Option';
    const wrapper = document.createElement('div');
    wrapper.className = 'buga-sticky-control';

    const select = document.createElement('select');
    select.className = 'buga-sticky-select';
    select.dataset.optionName = optionName;
    select.setAttribute('aria-label', optionName);

    for (const nativeOption of Array.from(nativeSelect.options)) {
      const option = document.createElement('option');
      option.value = nativeOption.value;
      option.textContent = nativeOption.textContent || nativeOption.value;
      option.selected = nativeOption.selected;
      option.disabled = nativeOption.disabled;
      select.appendChild(option);
    }

    select.addEventListener('change', () => {
      nativeSelect.value = select.value;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    });

    wrapper.appendChild(select);
    return wrapper;
  };

  const createQuantity = (sticky) => {
    const productForm = getProductForm(sticky);
    const mainInput = productForm?.querySelector('[ref="quantityInput"]');
    if (!(mainInput instanceof HTMLInputElement)) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'buga-sticky-quantity';

    const input = document.createElement('input');
    input.className = 'buga-sticky-quantity__input';
    input.type = 'number';
    input.inputMode = 'numeric';
    input.setAttribute('aria-label', 'Quantity');
    input.min = mainInput.min || '1';
    input.step = mainInput.step || '1';
    if (mainInput.max) input.max = mainInput.max;
    input.value = mainInput.value || input.min;

    const syncToMain = () => {
      let quantity = Number.parseInt(input.value || input.min || '1', 10) || 1;
      const min = Number.parseInt(input.min || '1', 10) || 1;
      const max = Number.parseInt(input.max || '0', 10) || 0;
      quantity = Math.max(min, quantity);
      if (max > 0) quantity = Math.min(max, quantity);
      input.value = String(quantity);
      mainInput.value = String(quantity);
      mainInput.dispatchEvent(new Event('input', { bubbles: true }));
      mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    input.addEventListener('input', syncToMain);
    input.addEventListener('change', syncToMain);
    mainInput.addEventListener('input', () => {
      if (document.activeElement !== input) input.value = mainInput.value;
    });

    wrapper.appendChild(input);
    return wrapper;
  };

  const enhance = (sticky) => {
    const bar = sticky.querySelector('[ref="stickyBar"]');
    if (!(bar instanceof HTMLElement)) return;
    bar.classList.add(STYLE_CLASS);

    if (bar.querySelector(`.${CONTROLS_CLASS}`)) return;

    const controls = document.createElement('div');
    controls.className = CONTROLS_CLASS;
    controls.setAttribute('aria-label', 'Product options and quantity');

    const picker = getVariantPicker(sticky);
    if (picker) {
      const groups = Array.from(picker.querySelectorAll('.variant-option'));
      for (const group of groups) {
        const radios = Array.from(group.querySelectorAll('input[data-option-name]'));
        let control = null;
        if (radios.length > 1) control = createSelectFromRadios(sticky, radios);
        if (!control) control = createSelectFromNativeSelect(group.querySelector('select'));
        if (control) controls.appendChild(control);
      }
    }

    const quantity = createQuantity(sticky);
    if (quantity) controls.appendChild(quantity);

    if (!controls.childElementCount) return;
    const price = bar.querySelector('.sticky-add-to-cart__price');
    if (price) bar.insertBefore(controls, price);
    else bar.appendChild(controls);
  };

  const watch = (sticky) => {
    if (!(sticky instanceof HTMLElement) || sticky.dataset.bugaSmartStickyReady === 'true') return;
    sticky.dataset.bugaSmartStickyReady = 'true';
    enhance(sticky);

    const bar = sticky.querySelector('[ref="stickyBar"]');
    if (!(bar instanceof HTMLElement)) return;

    const observer = new MutationObserver(() => {
      if (!bar.querySelector(`.${CONTROLS_CLASS}`)) requestAnimationFrame(() => enhance(sticky));
    });
    observer.observe(bar, { childList: true, subtree: false });
  };

  const init = () => document.querySelectorAll('sticky-add-to-cart').forEach(watch);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  const pageObserver = new MutationObserver(init);
  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
